import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/axios'
import { API_ROUTES } from '@/constants/routes'

// ── Order-on-payment checkout (active flow) ─────────────────────────────────
//
// The order is created ONLY when the payment confirms. `POST /api/checkout/pay`
// takes the cart contents (durable pre-payment state), creates a CheckoutSession
// snapshot, runs the Mercado Pago payment, and returns the order only once it
// exists. The cart stays intact until an order is created (rejection,
// abandonment, etc.), so the buyer can resume from the cart on any device.

/**
 * 3DS challenge payload returned by the backend on a `pending_challenge`
 * response (HTTP 200). It carries exactly what the MP Status Screen Brick
 * needs to render the bank challenge — no raw MP body, no secrets.
 */
export interface MercadoPagoThreeDs {
    /** Bank ACS challenge URL — forwarded as `externalResourceURL` to the Brick. */
    external_resource_url: string
    /** Challenge request token — forwarded as `creq` to the Brick. */
    creq: string
}

/** One line of the cart snapshot sent to `/api/checkout/pay`. */
export interface CheckoutPayItem {
    project: string
    front_page: number
    back_page: number | null
    material: number
    size: number
    quantity_millares: number
}

/** Card + cart payload for `POST /api/checkout/pay`. */
export interface CheckoutPayPayload {
    /** The cart items to charge (the backend builds the immutable snapshot). */
    items: CheckoutPayItem[]
    /** Structured shipping address id (FK to users.Address). */
    shippingAddress: number
    /** Optional order notes. */
    notes?: string
    /** Short-lived card token produced by the MP Card Payment Brick. */
    token: string
    /** Card brand (e.g. 'visa', 'master', 'amex'). */
    paymentMethodId: string
    /** Issuer (bank) id — optional, supplied by the Brick when known. */
    issuerId?: string
    /** Installments count (1 = pago al contado). */
    installments: number
    /** MP device fingerprint (window.MP_DEVICE_SESSION_ID) — cuts rejections. */
    deviceId?: string
    /** Payer data captured by the Brick. */
    payer: {
        email: string
        identification: { type: string; number: string }
    }
}

/**
 * Discriminated response from `POST /api/checkout/pay` (HTTP 200 for every
 * branch except a hard rejection, which is a 402 with a safe `{detail, code}`
 * body handled in the catch branch).
 *
 * - approved (`paid: true`) → the order now exists; the backend spreads the
 *   full OrderDetail (which carries its own `status` like "pending" awaiting
 *   admin confirmation, plus `uuid`/`order_number`). Success is discriminated
 *   by `paid === true`, NOT by a `status: 'approved'` field.
 * - `pending_challenge` → no order yet; mount the Status Screen Brick with
 *   `three_ds`, then poll `session/<session_uuid>/status` for the outcome.
 * - `pending` / `in_process` / `processing` → MP is reviewing or a payment is
 *   already in flight; "te confirmaremos por correo".
 */
export type CheckoutPayResponse =
    | {
          /** Approved: the order exists. Backend also spreads OrderDetail. */
          paid: true
          order_number: string
          /** Order uuid — used to route to the order detail page. */
          uuid?: string
          session_uuid?: string
      }
    | {
          status: 'pending_challenge'
          session_uuid: string
          three_ds: MercadoPagoThreeDs
          /** MP payment id — needed to mount the Status Screen Brick. */
          payment_id?: string
      }
    | {
          status: 'pending' | 'in_process' | 'processing'
          session_uuid: string
      }

/**
 * POST /api/checkout/pay — the single entry point for placing an order. It
 * creates a payment against the cart snapshot and only materialises the order
 * once the payment confirms. We invalidate orders on every settle so the
 * storefront reflects ground truth (an approved payment creates the order).
 */
export function useCheckoutPay() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload: CheckoutPayPayload): Promise<CheckoutPayResponse> => {
            const { data } = await api.post<CheckoutPayResponse>(API_ROUTES.checkoutPay, {
                items: payload.items,
                shipping_address: payload.shippingAddress,
                ...(payload.notes ? { notes: payload.notes } : {}),
                token: payload.token,
                payment_method_id: payload.paymentMethodId,
                issuer_id: payload.issuerId || '',
                installments: payload.installments,
                device_id: payload.deviceId || '',
                payer: payload.payer,
            })
            return data
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['orders'] })
        },
    })
}

/** Status of a checkout session, polled after a 3DS challenge resolves. */
export interface CheckoutSessionStatusResponse {
    status: 'processing' | 'paid' | 'failed' | 'expired'
    /** Present once the order exists (status === 'paid'). */
    order_number?: string
}

/**
 * GET /api/checkout/session/<uuid>/status — polled while a session is still
 * processing (after the buyer resolves a 3DS challenge) until it reaches a
 * terminal state: `paid` (the webhook created the order) or `failed`/`expired`
 * (no order, cart intact). Disabled until a `uuid` and `enabled` flag are set.
 */
export function useCheckoutSessionStatus(uuid: string | null, enabled: boolean) {
    return useQuery({
        queryKey: ['payments', 'session', uuid] as const,
        queryFn: async (): Promise<CheckoutSessionStatusResponse> => {
            const { data } = await api.get<CheckoutSessionStatusResponse>(
                API_ROUTES.checkoutSessionStatus(uuid!),
            )
            return data
        },
        enabled: enabled && !!uuid,
        // Poll while the session is still processing; stop once terminal.
        refetchInterval: (query): number | false => {
            const status = query.state.data?.status
            const terminal =
                status === 'paid' || status === 'failed' || status === 'expired'
            return terminal ? false : 2500
        },
        // Always hit the network — the session state changes server-side.
        staleTime: 0,
        gcTime: 0,
        retry: false,
    })
}

// ── Culqi (dormant gateway) ─────────────────────────────────────────────────

export interface CulqiOrderResponse {
    culqi_order_id: string
    public_key: string
    /** Amount in céntimos (S/ 1.00 = 100). */
    amount: number
    currency: string
    email: string
    order_number: string
}

export interface CulqiChargeResponse {
    paid: boolean
    order_number: string
    charge_id: string
}

/**
 * Create (or reuse) a Culqi Order for the given Django order. The Culqi Order
 * is what enables every payment method (card, Yape, PagoEfectivo, wallets…).
 */
export function useCulqiOrder() {
    return useMutation({
        mutationFn: async ({ orderUuid }: { orderUuid: string }) => {
            const { data } = await api.post<CulqiOrderResponse>(
                API_ROUTES.culqiOrder,
                { order_uuid: orderUuid },
            )
            return data
        },
    })
}

/** Charge a Culqi card/Yape token for an order — synchronous and authoritative. */
export function useCulqiCharge() {
    return useMutation({
        mutationFn: async ({ orderUuid, tokenId }: { orderUuid: string; tokenId: string }) => {
            const { data } = await api.post<CulqiChargeResponse>(
                API_ROUTES.culqiCharge,
                { order_uuid: orderUuid, token_id: tokenId },
            )
            return data
        },
    })
}

// ── Izipay (dormant gateway — kept for fallback) ────────────────────────────

export interface CreateTokenResponse {
    formToken: string
}

export interface VerifyPaymentPayload {
    kr_hash: string
    kr_hash_key: string
    kr_answer: Record<string, unknown>
}

export interface VerifyPaymentResponse {
    status: string
    order_number: string
    paid: boolean
}

export function useIzipayToken() {
    return useMutation({
        mutationFn: async ({ orderUuid }: { orderUuid: string }) => {
            const { data } = await api.post<CreateTokenResponse>(
                API_ROUTES.izipayCreateToken,
                { order_uuid: orderUuid }
            )
            return data
        },
    })
}

export function useVerifyPayment() {
    return useMutation({
        mutationFn: async (payload: VerifyPaymentPayload) => {
            const { data } = await api.post<VerifyPaymentResponse>(
                API_ROUTES.izipayVerify,
                payload
            )
            return data
        },
    })
}
